import {
  type TranscriptSegment,
  computeConversationAnalytics,
  formatMonologueDuration,
  mapApiConversationAnalytics,
} from '@/utils/conversationAnalytics';

export interface ExportPdfOptions {
  data: unknown;
  hasMeetingNotes?: boolean;
  refinedID?: string;
  aiPersonaName?: string;
  meetingDate?: string;
  userName?: string;
  logoUrl?: string;
}

const getSemanticColor = (score: number) => {
  if (score >= 75) return '#16a34a'; // Green
  if (score >= 50) return '#d97706'; // Amber / Orange
  return '#dc2626'; // Red
};

const getStatusLabel = (score: number) => {
  if (score >= 85) return 'Excellent';
  if (score >= 70) return 'Good';
  if (score >= 50) return 'Average';
  return 'Needs Work';
};

const parsePotentialObject = (value: unknown): Record<string, unknown> | null => {
  if (typeof value === 'object' && value !== null) return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) return parsed as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return null;
};

const parseScoreNumber = (val: unknown): number | undefined => {
  if (typeof val === 'number' && !isNaN(val)) return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
  }
  if (typeof val === 'object' && val !== null) {
    const obj = val as Record<string, unknown>;
    return (
      parseScoreNumber(obj.overall) ??
      parseScoreNumber(obj.overall_percentange) ??
      parseScoreNumber(obj.overallPercentage) ??
      parseScoreNumber(obj.score)
    );
  }
  return undefined;
};

const asStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map((item) => String(item));
  if (typeof value === 'string') return [value];
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if (Array.isArray(obj.summary)) return obj.summary.map((item) => String(item));
    if (typeof obj.summary === 'string') return [obj.summary];
    return Object.values(obj)
      .filter((item) => item != null)
      .map((item) => String(item));
  }
  return [];
};

const getPitchsterSummaryItems = (rawData: Record<string, unknown> | null | undefined) => {
  if (!rawData) return [];
  const aiData = (rawData.aiData as Record<string, unknown> | undefined) || {};
  const rawSummarySource =
    rawData.ai_summary ??
    rawData.aiFeedback ??
    rawData.feedback ??
    rawData.summary ??
    aiData.summary ??
    rawData.meetingNotes;
  const summarySource = parsePotentialObject(rawSummarySource) ?? rawSummarySource;

  return asStringArray(summarySource).filter((item) => item.trim().length > 0);
};


const renderVideoAnalysisSection = (
  rawData: Record<string, unknown> | null | undefined,
  icons: Record<string, unknown>,
) => {
  if (!rawData) return '';
  const aiData = (rawData.aiData as Record<string, unknown> | undefined) || {};
  const aiSummaryObj =
    parsePotentialObject(rawData.ai_summary) ??
    parsePotentialObject(rawData.aiFeedback) ??
    parsePotentialObject(rawData.feedback);
  const videoAnalysis =
    rawData.videoAnalysis ??
    aiData.videoAnalysis ??
    (rawData.feedback as Record<string, unknown> | undefined)?.videoAnalysis ??
    aiSummaryObj?.videoAnalysis;
  if (!videoAnalysis) return '';

  const items = Array.isArray(videoAnalysis)
    ? videoAnalysis
    : typeof videoAnalysis === 'string'
      ? [videoAnalysis]
      : Object.values(videoAnalysis as Record<string, unknown>);

  const itemsHtml = items
    .filter((item) => item != null)
    .map(
      (item) =>
        `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px; color: #334155;">${String(item)}</li>`,
    )
    .join('');

  if (!itemsHtml) return '';

  const categoryIcons = icons.categoryIcons as Record<string, string> | undefined;

  return `
    <div class="info-card" style="margin-bottom: 14px;">
      <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 16px; padding: 20px 24px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
          <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${categoryIcons?.digital_twin_video_analytics || ''}</div>
          <span style="font-weight: 800; font-size: 18px; color: #1d4ed8;">Video Analysis</span>
        </div>
        <ul style="margin: 0; padding-left: 24px;">${itemsHtml}</ul>
      </div>
    </div>
  `;
};

export const exportMeetingPdf = ({
  data,
  hasMeetingNotes: passedHasMeetingNotes,
  aiPersonaName,
  meetingDate,
  userName,
  logoUrl,
}: ExportPdfOptions) => {
  if (!data) return;

  const dataObj = typeof data === 'object' && data !== null ? (data as Record<string, unknown>) : {};
  const rawData = (dataObj.data as Record<string, unknown> | undefined) ?? dataObj;
  const aiDataObj = (rawData.aiData as Record<string, unknown> | undefined) || {};

  const rawDataAiSummary = parsePotentialObject(rawData.ai_summary) ?? {};
  const rawDataFeedback = parsePotentialObject(rawData.feedback) ?? {};

  const categoryScores = parsePotentialObject(rawData.categoryScores) ?? {};

  const aiScore =
    (rawData.scoringRaw as Record<string, unknown> | undefined) ||
    (rawData.ai_score as Record<string, unknown> | undefined) ||
    (rawData.scoring as Record<string, unknown> | undefined) ||
    (categoryScores.scores as Record<string, unknown> | undefined) ||
    (rawDataAiSummary.scoring as Record<string, unknown> | undefined) ||
    (rawDataFeedback.scores as Record<string, unknown> | undefined) ||
    {};

  const overallPercentage = Math.round(
    parseScoreNumber(rawData.overallScore) ??
      parseScoreNumber(rawData.overallPercentage) ??
      parseScoreNumber(rawData.score) ??
      parseScoreNumber(rawData.aiScore) ??
      parseScoreNumber(rawData.ai_score) ??
      parseScoreNumber(aiScore.overall_percentange) ??
      parseScoreNumber(aiScore.overallPercentage) ??
      parseScoreNumber(rawDataAiSummary.scoring) ??
      parseScoreNumber(categoryScores.overallScore) ??
      0,
  );

  const aiSummaryObj =
    parsePotentialObject(rawData.ai_summary) ??
    parsePotentialObject(rawData.aiFeedback) ??
    parsePotentialObject(rawData.feedback) ??
    {};

  const coachingInsightsSource =
    parsePotentialObject(
      aiDataObj.coachingInsights ??
        aiSummaryObj.coachingInsights ??
        rawData.coachingInsights ??
        (rawDataFeedback.coachingInsights as unknown) ??
        ((parsePotentialObject(rawData.aiFeedback) as Record<string, unknown> | null)?.coachingInsights as unknown),
    ) || {};

  const coachingSummarySource =
    (aiDataObj.coachingInsights as Record<string, unknown> | undefined)?.aiCoachingSummary ||
    (aiSummaryObj.aiCoachingSummary as string | undefined) ||
    (rawData.aiCoachingSummary as string | undefined) ||
    (rawDataFeedback.aiCoachingSummary as string | undefined) ||
    (coachingInsightsSource.aiCoachingSummary as string | undefined) ||
    '';

  const coaching = {
    ...coachingInsightsSource,
    aiCoachingSummary: coachingSummarySource,
  };

  const dealIntel =
    (aiDataObj.dealIntelligence as Record<string, unknown> | undefined) ||
    (aiSummaryObj.dealIntelligence as Record<string, unknown> | undefined) ||
    (rawData.dealIntelligence as Record<string, unknown> | undefined) ||
    (rawDataFeedback.dealIntelligence as Record<string, unknown> | undefined) ||
    {};

  const objections =
    aiDataObj.objectionsDetected ||
    aiSummaryObj.objectionsDetected ||
    rawData.objectionsDetected ||
    rawDataFeedback.objectionsDetected ||
    [];

  const buyingSignals =
    aiDataObj.buyingSignalsDetected ||
    aiSummaryObj.buyingSignalsDetected ||
    rawData.buyingSignalsDetected ||
    rawDataFeedback.buyingSignalsDetected ||
    [];

  const missedOpps =
    aiDataObj.missedOpportunities ||
    aiSummaryObj.missedOpportunities ||
    rawData.missedOpportunities ||
    rawDataFeedback.missedOpportunities ||
    [];

  const buyingSignalInsight =
    (aiDataObj.buyingSignalInsight as string | undefined) ||
    (aiSummaryObj.buyingSignalInsight as string | undefined) ||
    (rawData.buyingSignalInsight as string | undefined) ||
    (rawDataFeedback.buyingSignalInsight as string | undefined) ||
    '';

  const objectionFeedback =
    (aiDataObj.objectionFeedback as string | undefined) ||
    (aiSummaryObj.objectionFeedback as string | undefined) ||
    (rawData.objectionFeedback as string | undefined) ||
    (rawDataFeedback.objectionFeedback as string | undefined) ||
    '';

  const missedOpportunityAdvice =
    (aiDataObj.missedOpportunityAdvice as string | undefined) ||
    (aiSummaryObj.missedOpportunityAdvice as string | undefined) ||
    (rawData.missedOpportunityAdvice as string | undefined) ||
    (rawDataFeedback.missedOpportunityAdvice as string | undefined) ||
    '';

  const meetingNotes =
    rawData.meetingNotes ||
    aiSummaryObj.meetingNotes ||
    (rawData.ai_summary as Record<string, unknown> | undefined)?.meetingNotes ||
    (rawData.feedback as Record<string, unknown> | undefined)?.meetingNotes ||
    rawDataFeedback.meetingNotes ||
    rawData.notes ||
    rawData.ai_notes;

  const hasMeetingNotes = Boolean(
    passedHasMeetingNotes ||
      (meetingNotes &&
        (typeof meetingNotes === 'string'
          ? meetingNotes.trim().length > 0
          : typeof meetingNotes === 'object' && Object.keys(meetingNotes).length > 0)),
  );


  const contact =
    parsePotentialObject(rawData.companyContact || rawData.customerContact || rawData.contact) || {};
  const customerFirstName = String(contact.firstName || rawData.customerFirstName || '');
  const customerLastName = String(contact.lastName || rawData.customerLastName || '');
  let customerFullName = `${customerFirstName} ${customerLastName}`.trim();
  if (
    !customerFullName &&
    typeof aiPersonaName === 'string' &&
    !aiPersonaName.startsWith('Assistant') &&
    !aiPersonaName.startsWith('Customer')
  ) {
    customerFullName = aiPersonaName.split('(')[0].trim();
  }
  if (!customerFullName || customerFullName === 'N/A') {
    customerFullName = String(
      (rawData.customerContact as Record<string, unknown> | undefined)?.fullName ||
        rawData.customerName ||
        '',
    );
  }

  const rawUser = parsePotentialObject(rawData.User || rawData.user) || {};
  const customerTitle = String(
    contact.jobTitle ||
      contact.position ||
      (Array.isArray(contact.buyerPersona) ? contact.buyerPersona[0] : '') ||
      rawData.jobTitle ||
      '',
  );

  const scenarioObj = (rawData.scenario as Record<string, unknown> | undefined) || {};
  const isScenario = Boolean(rawData.isScenario || rawData.meetingType === 'scenario' || scenarioObj.id || scenarioObj.title);
  const isPitch = Boolean(rawData.isPitch || rawData.pitchTitle || rawData.pitchType);


  const companyName = String(
    isScenario
      ? scenarioObj.title || rawData.company || ''
      : isPitch
        ? rawData.pitchTitle || rawData.company || ''
        : contact.companyName || contact.company || rawData.companyName || rawData.company || rawData.tenantName || '',
  );

  const repUserName =
    userName ||
    String(
      rawData.salesRep ||
        rawUser.name ||
        rawData.userName ||
        rawData.createdByName ||
        rawData.repName ||
        'Sales Representative',
    );

  // Date & Time formatting
  const rawDate = (meetingDate || rawData.createdAt || rawData.date || rawData.updatedAt) as
    | string
    | number
    | Date
    | undefined;
  const formattedDateTime = rawDate
    ? new Date(rawDate).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : new Date().toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

  // Duration formatting
  const rawDuration =
    rawData.duration ||
    rawData.recordingDuration ||
    rawData.audioDuration ||
    rawData.durationMs ||
    rawData.duration_seconds ||
    rawData.time_spent;

  const formatDuration = (val: unknown) => {
    if (!val) return '';
    let seconds = 0;
    if (typeof val === 'number') {
      seconds = val > 10000 ? Math.floor(val / 1000) : val;
    } else if (typeof val === 'string') {
      const num = parseInt(val, 10);
      if (!isNaN(num)) seconds = num > 10000 ? Math.floor(num / 1000) : num;
      else return val;
    }
    if (seconds <= 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formattedDuration = formatDuration(rawDuration);

  // SVG Icons
  const icons = {
    brain: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/><path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/><path d="M12 5v13"/></svg>`,
    sparkles: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>`,
    trendingUp: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`,
    alertTriangle: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
    lightbulb: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>`,
    barChart: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>`,
    fileText: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>`,
    users: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
    messageSquare: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
    activity: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
    mic: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>`,
    gauge: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 14 4-4"/><path d="M3.34 19a10 10 0 1 1 17.32 0"/></svg>`,
    zap: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
    categoryIcons: {
      opening: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M13 4h3a2 2 0 0 1 2 2v14"/><path d="M2 20h20"/><path d="M13 20V4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v16"/></svg>`,
      discovery: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
      pitch_delivery: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M2 3h20"/><path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3"/><path d="m7 21 5-5 5 5"/></svg>`,
      engagement: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="m11 17 2 2a1 1 0 0 0 1.4 0l4.3-4.3a1 1 0 0 0 0-1.4L15 9"/><path d="m15 13 2-2"/><path d="m7 11 2 2"/><path d="m11 7 2 2"/></svg>`,
      confidence_and_tone: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`,
      closing: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>`,
      digital_twin_video_analytics: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>`,
      overall_impression: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
    } as Record<string, string>,
  };

  // 1. Deal Intelligence Render with UI Color Logic
  const renderDealIntelligence = () => {
    const closeProbRaw = dealIntel.closeProbability;
    const closeProb =
      typeof closeProbRaw === 'number'
        ? closeProbRaw
        : typeof closeProbRaw === 'string' && !Number.isNaN(Number(closeProbRaw))
          ? Number(closeProbRaw)
          : undefined;
    const prog = dealIntel.opportunityProgression as string | undefined;
    const risk = dealIntel.riskLevel as string | undefined;
    const riskReason = dealIntel.riskReason as string | undefined;

    const overallColor = getSemanticColor(overallPercentage);
    const overallStatus = getStatusLabel(overallPercentage);

    const closeProbColor = typeof closeProb === 'number' ? getSemanticColor(closeProb) : '#2563eb';

    const getProgColor = (p?: string) => {
      const val = (p || '').toLowerCase();
      if (val === 'high') return '#16a34a';
      if (val === 'low') return '#dc2626';
      return '#d97706';
    };
    const progColor = getProgColor(prog);

    const getRiskColor = (r?: string) => {
      const val = (r || '').toLowerCase();
      if (val === 'low') return '#16a34a';
      if (val === 'high') return '#dc2626';
      return '#d97706';
    };
    const riskColor = getRiskColor(risk);

    return `
      <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 16px; padding: 20px 22px; margin-bottom: 24px; box-sizing: border-box; width: 100%; page-break-inside: avoid; break-inside: avoid;">
        <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 18px;">
          <div style="background: #dbeafe; padding: 7px; border-radius: 8px; display: flex; align-items: center;">${icons.brain}</div>
          <span style="font-size: 20px; font-weight: 800; color: #1d4ed8;">Deal Intelligence</span>
        </div>

        <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; width: 100%; box-sizing: border-box;">
          <!-- Overall Performance Gauge Ring with UI Color Logic -->
          <div class="deal-metric-card" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 10px; text-align: center; display: flex; flex-direction: column; justify-content: space-between; align-items: center; min-width: 0; box-sizing: border-box; break-inside: avoid !important; page-break-inside: avoid !important;">
            <div style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; margin-bottom: 4px;">Overall Performance</div>
            <div style="position: relative; display: inline-flex; align-items: center; justify-content: center; margin: 2px 0; width: 100%; max-width: 130px;">
              <svg width="100%" viewBox="0 0 200 120" style="max-width: 130px; height: auto;">
                <path d="M20,100 A80,80 0 0 1 180,100" fill="none" stroke="#e2e8f0" stroke-width="14" stroke-linecap="round" />
                <path d="M20,100 A80,80 0 0 1 180,100" fill="none" stroke="${overallColor}" stroke-width="14" stroke-linecap="round" stroke-dasharray="${(overallPercentage / 100) * 251.2} 251.2" />
              </svg>
              <div style="position: absolute; top: 52%; left: 50%; transform: translate(-50%, -40%); font-size: 18px; font-weight: 800; color: ${overallColor};">
                ${overallPercentage}%
              </div>
            </div>
            <div style="font-size: 12px; font-weight: 800; color: ${overallColor}; margin-top: 2px;">${overallStatus}</div>
          </div>

          <!-- Close Probability with UI Color Logic -->
          <div class="deal-metric-card" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 12px; display: flex; flex-direction: column; justify-content: space-between; min-width: 0; box-sizing: border-box; break-inside: avoid !important; page-break-inside: avoid !important;">
            <div>
              <div style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; margin-bottom: 6px;">Close Probability</div>
              <div style="font-size: 24px; font-weight: 800; color: ${closeProbColor}; margin-bottom: 6px;">${typeof closeProb === 'number' ? `${closeProb}%` : 'N/A'}</div>
              <div style="background: #e2e8f0; height: 6px; border-radius: 999px; overflow: hidden; margin-bottom: 8px;">
                <div style="background: ${closeProbColor}; width: ${typeof closeProb === 'number' ? closeProb : 0}%; height: 100%;"></div>
              </div>
            </div>
            ${riskReason ? `<div style="font-size: 11px; color: #475569; line-height: 1.3;">${riskReason}</div>` : ''}
          </div>

          <!-- Progression with UI Color Logic -->
          <div class="deal-metric-card" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 12px; display: flex; flex-direction: column; justify-content: space-between; min-width: 0; box-sizing: border-box; break-inside: avoid !important; page-break-inside: avoid !important;">
            <div>
              <div style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; margin-bottom: 6px;">Progression</div>
              <div style="font-size: 22px; font-weight: 800; color: ${progColor}; margin-bottom: 6px;">${prog || 'Moderate'}</div>
              <div style="background: #e2e8f0; height: 6px; border-radius: 999px; overflow: hidden; margin-bottom: 8px;">
                <div style="background: ${progColor}; width: ${prog?.toLowerCase() === 'high' ? 85 : prog?.toLowerCase() === 'low' ? 35 : 65}%; height: 100%;"></div>
              </div>
            </div>
            <div style="font-size: 11px; color: #475569; line-height: 1.3;">Steady movement toward next stage.</div>
          </div>

          <!-- Risk Level with UI Color Logic -->
          <div class="deal-metric-card" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 12px; display: flex; flex-direction: column; justify-content: space-between; min-width: 0; box-sizing: border-box; break-inside: avoid !important; page-break-inside: avoid !important;">
            <div>
              <div style="font-size: 11px; font-weight: 700; color: #1e40af; text-transform: uppercase; margin-bottom: 6px;">Risk Level</div>
              <div style="font-size: 22px; font-weight: 800; color: ${riskColor}; margin-bottom: 6px;">${risk || 'Low'}</div>
              <div style="background: #e2e8f0; height: 6px; border-radius: 999px; overflow: hidden; margin-bottom: 8px;">
                <div style="background: ${riskColor}; width: ${risk?.toLowerCase() === 'high' ? 85 : risk?.toLowerCase() === 'medium' ? 50 : 25}%; height: 100%;"></div>
              </div>
            </div>
            <div style="font-size: 11px; color: #475569; line-height: 1.3;">Deal health evaluation.</div>
          </div>
        </div>
      </div>
    `;
  };

  // 2. Skill & Category Scoring Breakdown
  const renderCategoryScoresBreakdown = () => {
    const categories: Record<string, { label: string; iconKey: string }> = {
      opening: { label: 'Opening Phase', iconKey: 'opening' },
      discovery: { label: 'Discovery & Needs', iconKey: 'discovery' },
      pitch_delivery: { label: 'Pitch Delivery', iconKey: 'pitch_delivery' },
      engagement: { label: 'Customer Engagement', iconKey: 'engagement' },
      confidence_and_tone: { label: 'Confidence & Tone', iconKey: 'confidence_and_tone' },
      closing: { label: 'Closing & Urgency', iconKey: 'closing' },
      digital_twin_video_analytics: {
        label: 'Video Analytics',
        iconKey: 'digital_twin_video_analytics',
      },
      overall_impression: { label: 'Overall Impression', iconKey: 'overall_impression' },
    };

    let itemsHtml = '';

    Object.entries(categories).forEach(([key, meta]) => {
      const catObj = aiScore[key];
      if (catObj && typeof catObj === 'object') {
        const values = Object.values(catObj as Record<string, unknown>).filter(
          (v) => typeof v === 'number',
        ) as number[];
        const catScore100 = values.length
          ? Math.round((values.reduce((a, b) => a + b, 0) / (values.length * 5)) * 100)
          : 0;

        const color = getSemanticColor(catScore100);
        const statusLabel = getStatusLabel(catScore100);

        const subItems = Object.entries(catObj as Record<string, unknown>)
          .filter(([m, v]) => typeof v === 'number' && !m.toLowerCase().includes('token'))
          .map(([subKey, val]) => {
            const formattedKey = subKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
            return `
              <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; font-size: 11px; margin-bottom: 4px; color: #334155; border-bottom: 1px dashed #f1f5f9; padding-bottom: 2px;">
                <span style="flex: 1; min-width: 0; word-break: break-word;">${formattedKey}</span>
                <span style="font-weight: 800; color: #1e293b; margin-left: 8px; white-space: nowrap;">${String(val)}/5</span>
              </div>
            `;
          })
          .join('');

        const catIcon = icons.categoryIcons[meta.iconKey] || icons.barChart;

        itemsHtml += `
          <div class="category-score-card" style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 14px 12px; display: flex; flex-direction: column; justify-content: space-between; gap: 10px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); break-inside: avoid !important; page-break-inside: avoid !important;">
            <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid #e2e8f0; padding-bottom: 6px;">
              <div style="display: flex; align-items: center; gap: 6px; min-width: 0;">
                <div style="background: #eff6ff; padding: 4px; border-radius: 6px; display: flex; flex-shrink: 0;">${catIcon}</div>
                <span style="font-weight: 800; font-size: 14px; color: #1e293b;">${meta.label}</span>
              </div>
              <span style="font-weight: 800; font-size: 11px; color: ${color}; background: ${color}15; padding: 2px 7px; border-radius: 999px; flex-shrink: 0; margin-left: 4px;">${statusLabel}</span>
            </div>

            <div style="display: flex; align-items: center; gap: 12px;">
              <!-- Full Circle Score Ring with Color Logic -->
              <div style="position: relative; display: inline-flex; align-items: center; justify-content: center; width: 58px; height: 58px; flex-shrink: 0;">
                <svg width="58" height="58" viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" stroke-width="8" />
                  <circle cx="50" cy="50" r="40" fill="none" stroke="${color}" stroke-width="8" stroke-linecap="round" stroke-dasharray="${(catScore100 / 100) * 251.2} 251.2" transform="rotate(-90 50 50)" />
                </svg>
                <div style="position: absolute; font-size: 13px; font-weight: 800; color: ${color}; top: 50%; left: 50%; transform: translate(-50%, -50%);">
                  ${catScore100}%
                </div>
              </div>

              <!-- Sub metrics -->
              <div style="flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 4px;">
                ${subItems}
              </div>
            </div>
          </div>
        `;
      }
    });

    if (!itemsHtml) return '';

    return `
      <div style="margin-bottom: 24px;">
        <div class="section-title">
          <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.barChart}</div>
          <span>Skill & Category Scoring Breakdown</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
          ${itemsHtml}
        </div>
      </div>
    `;
  };

  // Coaching Went Well List (Green)
  const coachingAny = coaching as Record<string, unknown>;
  const wentWellItems = Array.isArray(coachingAny.wentWell)
    ? coachingAny.wentWell
    : Array.isArray(coachingAny.whatWentWell)
      ? coachingAny.whatWentWell
      : [];

  const wentWellList = wentWellItems
    .map((item: unknown) => {
      if (typeof item === 'string') {
        return `<li style="margin-bottom: 8px; line-height: 1.6; color: #166534; font-size: 14px;">${item}</li>`;
      }
      const obj = item as Record<string, unknown> | null | undefined;
      const title = String(obj?.title || obj?.heading || '');
      const description = String(obj?.description || obj?.detail || obj?.text || '');
      return `<li style="margin-bottom: 8px; line-height: 1.6; color: #166534; font-size: 14px;"><strong>${title}${description ? ':' : ''}</strong> ${description || title}</li>`;
    })
    .join('');

  // Coaching Areas for Improvement / Needs Improvement List (Red)
  const needsImprovementItems = Array.isArray(coachingAny.needsImprovement)
    ? coachingAny.needsImprovement
    : Array.isArray(coachingAny.areasForImprovement)
      ? coachingAny.areasForImprovement
      : Array.isArray(coachingAny.areasOfImprovement)
        ? coachingAny.areasOfImprovement
        : [];

  const needsImprovementList = needsImprovementItems
    .map((item: unknown) => {
      if (typeof item === 'string') {
        return `<li style="margin-bottom: 8px; line-height: 1.6; color: #991b1b; font-size: 14px;">${item}</li>`;
      }
      const obj = item as Record<string, unknown> | null | undefined;
      const title = String(obj?.title || obj?.heading || '');
      const description = String(obj?.description || obj?.detail || obj?.text || '');
      return `<li style="margin-bottom: 8px; line-height: 1.6; color: #991b1b; font-size: 14px;"><strong>${title}${description ? ':' : ''}</strong> ${description || title}</li>`;
    })
    .join('');

  // Coaching How to Improve List (Blue)
  const howToImproveItems = Array.isArray(coachingAny.howToImprove)
    ? coachingAny.howToImprove
    : Array.isArray(coachingAny.how_to_improve)
      ? coachingAny.how_to_improve
      : [];

  const howToImproveList = howToImproveItems
    .map((item: unknown) => {
      const obj =
        typeof item === 'object' && item !== null ? (item as Record<string, unknown>) : null;
      const title = String(
        obj?.title || obj?.heading || (typeof item === 'string' ? item : 'Improvement'),
      );
      const description = String(obj?.description || obj?.detail || obj?.text || '');
      return `<li style="margin-bottom: 10px; line-height: 1.6; color: #1d4ed8; font-size: 14px;"><strong>${title}${description ? ':' : ''}</strong> ${description || (typeof item === 'string' ? item : '')}</li>`;
    })
    .join('');

  const renderHowToImproveSection = () => {
    if (!howToImproveList) return '';

    return `
      <div class="info-card" style="margin-top: 14px;">
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 14px; padding: 16px 20px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.zap}</div>
            <span style="font-weight: 800; font-size: 17px; color: #1d4ed8;">How to Improve</span>
          </div>
          <ul style="margin: 0; padding-left: 22px; font-size: 13.5px; color: #1e40af;">${howToImproveList}</ul>
        </div>
      </div>
    `;
  };

  const renderPitchsterInsightsSection = () => {
    const coachingSummaryText = String(coaching.aiCoachingSummary || '').trim();
    const hasCoachingInsights =
      coachingSummaryText.length > 0 ||
      wentWellList.length > 0 ||
      needsImprovementList.length > 0 ||
      Boolean(howToImproveList);

    if (hasCoachingInsights) {
      return `
        <div class="coaching-section" style="margin-bottom: 24px;">
          <div class="section-title">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.sparkles}</div>
            <span>Pitchster Insights</span>
          </div>

          ${
            coachingSummaryText
              ? `
            <div class="info-card" style="background: #eff6ff; border-left: 5px solid #2563eb; border-radius: 10px; padding: 16px 20px; margin-bottom: 18px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
              <div style="font-size: 14px; line-height: 1.6; color: #1e40af;">${coachingSummaryText}</div>
            </div>
          `
              : ''
          }

          ${
            wentWellList || needsImprovementList
              ? `
            <div class="grid-2" style="margin-bottom: 18px;">
              ${
                wentWellList
                  ? `
                <div class="card-box" style="background: #f0fdf4; border: 1px solid #bbf7d0;">
                  <div style="font-weight: 800; font-size: 15px; color: #15803d; margin-bottom: 8px;">
                    ✅ Went Well
                  </div>
                  <ul style="margin: 0; padding-left: 20px;">${wentWellList}</ul>
                </div>
              `
                  : ''
              }

              ${
                needsImprovementList
                  ? `
                <div class="card-box" style="background: #fef2f2; border: 1px solid #fecaca;">
                  <div style="font-weight: 800; font-size: 15px; color: #b91c1c; margin-bottom: 8px;">
                    🎯 Needs Improvement
                  </div>
                  <ul style="margin: 0; padding-left: 20px;">${needsImprovementList}</ul>
                </div>
              `
                  : ''
              }
            </div>
          `
              : ''
          }

          ${renderHowToImproveSection()}
        </div>
      `;
    }

    const summaryItems = getPitchsterSummaryItems(rawData);
    if (summaryItems.length > 0) {
      const summaryHtml = summaryItems
        .map(
          (item) =>
            `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px; color: #334155;">${item}</li>`,
        )
        .join('');

      return `
        <div class="info-card" style="margin-bottom: 24px;">
          <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 16px; padding: 20px 24px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
            <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 12px;">
              <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.sparkles}</div>
              <span style="font-weight: 800; font-size: 18px; color: #1d4ed8;">Pitchster Insights</span>
            </div>
            <ul style="margin: 0; padding-left: 24px;">${summaryHtml}</ul>
          </div>
        </div>
      `;
    }

    return '';
  };

  // Buying Signals
  const renderBuyingSignals = () => {
    const insight =
      buyingSignalInsight ||
      (aiSummaryObj.buyingSignalInsight as string | undefined) ||
      (aiDataObj.buyingSignalInsight as string | undefined);
    const signals = Array.isArray(buyingSignals) ? buyingSignals : [];

    if (!insight && signals.length === 0) return '';

    const listHtml = signals
      .map((item: unknown) => {
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          const sig = String(obj.signal || obj.type || obj.title || JSON.stringify(item));
          const strength = obj.strength
            ? ` <span style="color: #2563eb; font-weight: 700;">[${String(obj.strength)} Strength]</span>`
            : '';
          return `<li style="margin-bottom: 6px; line-height: 1.5; font-size: 13.5px;">${sig}${strength}</li>`;
        }
        return `<li style="margin-bottom: 6px; line-height: 1.5; font-size: 13.5px;">${String(item)}</li>`;
      })
      .join('');

    return `
      <div class="info-card" style="margin-bottom: 16px;">
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 14px; padding: 16px 20px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.trendingUp}</div>
            <span style="font-weight: 800; font-size: 17px; color: #1d4ed8;">Buying Signals</span>
          </div>
          ${listHtml ? `<ul style="margin: 0; padding-left: 22px; font-size: 13.5px; color: #1e40af;">${listHtml}</ul>` : ''}
          ${insight ? `<div style="font-size: 13.5px; color: #1e40af; margin-top: 10px; ${listHtml ? 'padding-top: 10px; border-top: 1px dashed #bfdbfe;' : ''} line-height: 1.5;">${insight}</div>` : ''}
        </div>
      </div>
    `;
  };

  // Objections Detected
  const renderObjectionsDetected = () => {
    const feedback =
      objectionFeedback ||
      (aiSummaryObj.objectionFeedback as string | undefined) ||
      (aiDataObj.objectionFeedback as string | undefined);
    const items = Array.isArray(objections) ? objections : [];

    if (!feedback && items.length === 0) return '';

    const listHtml = items
      .map((item: unknown) => {
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          const type = String(obj.type || obj.objection || obj.title || JSON.stringify(item));
          const status = obj.status
            ? ` <span style="color: #2563eb; font-weight: 700;">(${String(obj.status)})</span>`
            : '';
          const details = obj.details
            ? `<div style="font-size: 12.5px; color: #3b82f6; margin-top: 3px;">${String(obj.details)}</div>`
            : '';
          return `<li style="margin-bottom: 8px; line-height: 1.5; font-size: 13.5px;"><strong>${type}</strong>${status}${details}</li>`;
        }
        return `<li style="margin-bottom: 8px; line-height: 1.5; font-size: 13.5px;">${String(item)}</li>`;
      })
      .join('');

    return `
      <div class="info-card" style="margin-bottom: 16px;">
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 14px; padding: 16px 20px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.alertTriangle}</div>
            <span style="font-weight: 800; font-size: 17px; color: #1d4ed8;">Objections & Concerns Raised</span>
          </div>
          ${listHtml ? `<ul style="margin: 0; padding-left: 22px; font-size: 13.5px; color: #1e40af;">${listHtml}</ul>` : ''}
          ${feedback ? `<div style="font-size: 13.5px; color: #1e40af; margin-top: 10px; ${listHtml ? 'padding-top: 10px; border-top: 1px dashed #bfdbfe;' : ''} line-height: 1.5;">${feedback}</div>` : ''}
        </div>
      </div>
    `;
  };

  // Missed Opportunities
  const renderMissedOpportunities = () => {
    const advice =
      missedOpportunityAdvice ||
      (aiDataObj.missedOpportunityAdvice as string | undefined) ||
      (aiSummaryObj.missedOpportunityAdvice as string | undefined) ||
      (rawData.missedOpportunityAdvice as string | undefined) ||
      (rawDataFeedback.missedOpportunityAdvice as string | undefined);

    const items = Array.isArray(missedOpps)
      ? missedOpps
      : typeof missedOpps === 'string'
        ? [missedOpps]
        : [];

    if (!advice && items.length === 0) return '';

    const listHtml = items
      .map((item: unknown) => {
        if (typeof item === 'object' && item !== null) {
          const obj = item as Record<string, unknown>;
          const opp = String(obj.opportunity || obj.title || JSON.stringify(item));
          const sug = obj.suggestion ? ` - <em>${String(obj.suggestion)}</em>` : '';
          return `<li style="margin-bottom: 8px; line-height: 1.5; font-size: 13.5px;">${opp}${sug}</li>`;
        }
        return `<li style="margin-bottom: 8px; line-height: 1.5; font-size: 13.5px;">${String(item)}</li>`;
      })
      .join('');

    return `
      <div class="info-card" style="margin-bottom: 16px;">
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 14px; padding: 16px 20px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.lightbulb}</div>
            <span style="font-weight: 800; font-size: 17px; color: #1d4ed8;">Missed Opportunities</span>
          </div>
          ${listHtml ? `<ul style="margin: 0; padding-left: 22px; font-size: 13.5px; color: #1e40af;">${listHtml}</ul>` : ''}
          ${advice ? `<div style="font-size: 13.5px; color: #1e40af; margin-top: 10px; ${listHtml ? 'padding-top: 10px; border-top: 1px dashed #bfdbfe;' : ''} line-height: 1.5;">${advice}</div>` : ''}
        </div>
      </div>
    `;
  };

  // Meeting Notes Section
  const renderMeetingNotesSection = () => {
    if (!hasMeetingNotes && !meetingNotes) return '';

    let parsedNotes = meetingNotes;
    if (typeof meetingNotes === 'string') {
      try {
        parsedNotes = JSON.parse(meetingNotes);
      } catch {
        parsedNotes = meetingNotes;
      }
    }

    let notesBody = '';

    if (typeof parsedNotes === 'string') {
      notesBody = `<p style="font-size: 14px; line-height: 1.6; color: #334155;">${parsedNotes}</p>`;
    } else if (typeof parsedNotes === 'object' && parsedNotes !== null) {
      const obj = parsedNotes as Record<string, unknown>;
      const titleText = obj.title ? String(obj.title) : '';
      const summaryText =
        obj.executiveSummary || (obj.title ? '' : obj.summary || obj.overview || obj.description);

      const keyPoints = obj.keyDiscussionPoints || obj.keyPoints || obj.highlights;
      const actionItems = obj.actionItems;
      const decisionsMade = obj.decisionsMade;
      const nextSteps = obj.nextSteps;
      const openQuestions = obj.openQuestions;

      if (titleText) {
        notesBody += `
          <div style="margin-bottom: 14px; background: #ffffff; padding: 10px 14px; border-radius: 8px; border-left: 4px solid #1d4ed8;">
            <h4 style="margin: 0; font-size: 15px; color: #1e3a8a; font-weight: 800;">${titleText}</h4>
          </div>
        `;
      }

      if (summaryText) {
        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Executive Summary</h4>
            <p style="margin: 0; font-size: 14px; line-height: 1.6; color: #334155;">${String(summaryText)}</p>
          </div>
        `;
      }

      if (Array.isArray(keyPoints) && keyPoints.length > 0) {
        const itemsHtml = keyPoints
          .map((pt: unknown) => {
            if (typeof pt === 'object' && pt !== null) {
              const ptObj = pt as Record<string, unknown>;
              const topic = ptObj.topic ? `<strong>${String(ptObj.topic)}:</strong> ` : '';
              const details = String(ptObj.details || ptObj.description || JSON.stringify(pt));
              return `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${topic}${details}</li>`;
            }
            return `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${String(pt)}</li>`;
          })
          .join('');

        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Key Discussion Points</h4>
            <ul style="margin: 0; padding-left: 24px; font-size: 14px; color: #334155;">
              ${itemsHtml}
            </ul>
          </div>
        `;
      }

      if (Array.isArray(decisionsMade) && decisionsMade.length > 0) {
        const decisionsHtml = decisionsMade
          .map(
            (d: unknown) =>
              `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${String(d)}</li>`,
          )
          .join('');

        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Decisions Made</h4>
            <ul style="margin: 0; padding-left: 24px; font-size: 14px; color: #334155;">
              ${decisionsHtml}
            </ul>
          </div>
        `;
      }

      if (Array.isArray(actionItems) && actionItems.length > 0) {
        const actionsHtml = actionItems
          .map((item: unknown) => {
            if (typeof item === 'object' && item !== null) {
              const actObj = item as Record<string, unknown>;
              const task = String(actObj.task || actObj.description || JSON.stringify(item));
              const owner = actObj.owner ? ` <em>(Owner: ${String(actObj.owner)})</em>` : '';
              const priority = actObj.priority
                ? ` <span style="font-weight: 700; color: #2563eb;">[${String(actObj.priority)}]</span>`
                : '';
              return `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${task}${owner}${priority}</li>`;
            }
            return `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${String(item)}</li>`;
          })
          .join('');

        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Action Items</h4>
            <ul style="margin: 0; padding-left: 24px; font-size: 14px; color: #334155;">
              ${actionsHtml}
            </ul>
          </div>
        `;
      }

      if (Array.isArray(nextSteps) && nextSteps.length > 0 && nextSteps !== actionItems) {
        const stepsHtml = nextSteps
          .map(
            (step: unknown) =>
              `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${String(step)}</li>`,
          )
          .join('');

        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Next Steps</h4>
            <ul style="margin: 0; padding-left: 24px; font-size: 14px; color: #334155;">
              ${stepsHtml}
            </ul>
          </div>
        `;
      }

      if (Array.isArray(openQuestions) && openQuestions.length > 0) {
        const questionsHtml = openQuestions
          .map(
            (q: unknown) =>
              `<li style="margin-bottom: 8px; line-height: 1.6; font-size: 14px;">${String(q)}</li>`,
          )
          .join('');

        notesBody += `
          <div style="margin-bottom: 18px;">
            <h4 style="margin: 0 0 8px 0; font-size: 15px; color: #1d4ed8; font-weight: 800;">Open Questions</h4>
            <ul style="margin: 0; padding-left: 24px; font-size: 14px; color: #334155;">
              ${questionsHtml}
            </ul>
          </div>
        `;
      }
    }

    if (!notesBody) return '';

    return `
      <div class="info-card" style="margin-bottom: 24px;">
        <div style="background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 16px; padding: 20px 22px; -webkit-box-decoration-break: clone; box-decoration-break: clone;">
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 16px;">
            <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.fileText}</div>
            <h3 style="margin: 0; font-size: 18px; color: #1d4ed8; font-weight: 800;">Meeting Notes</h3>
          </div>
          ${notesBody}
        </div>
      </div>
    `;
  };

  // Conversation Analytics Render (6 small compact boxes in 1 single row)
  const renderConversationAnalyticsSection = () => {
    const backendAnalytics = rawData.conversationAnalytics || rawData.conversationAnalyticsRaw;
    const rawAiSummary = parsePotentialObject(rawData.ai_summary) || {};

    const analytics =
      backendAnalytics != null
        ? mapApiConversationAnalytics(backendAnalytics)
        : computeConversationAnalytics(
            (rawData.transcriptJson as TranscriptSegment[] | undefined) ||
              (rawData.transcript_text ? undefined : undefined),
            0,
            (rawAiSummary.interruptionsCount as number | undefined) ??
              (rawData.interruptionsCount as number | undefined) ??
              0,
          );

    if (!analytics) return '';

    const talkVal = Math.round(analytics.talkListenRatio.talk);
    const listenVal = Math.round(analytics.talkListenRatio.listen);

    const items = [
      {
        icon: icons.users,
        label: 'Talk-to-Listen Ratio',
        value: `${talkVal} / ${listenVal}`,
        status: talkVal > listenVal ? 'Rep talked more' : listenVal > talkVal ? 'Customer talked more' : 'Balanced',
      },
      {
        icon: icons.messageSquare,
        label: 'Filler Words',
        value: `${analytics.fillerWordsPerMin}`,
        unit: 'per min',
        status: analytics.fillerWordsPerMin <= 6 ? 'Good' : 'Could improve',
      },
      {
        icon: icons.activity,
        label: 'Longest Monologue',
        value: formatMonologueDuration(analytics.longestMonologueSeconds),
        status: analytics.longestMonologueSeconds <= 90 ? 'Good' : 'Too long',
      },
      {
        icon: icons.mic,
        label: 'Interruptions',
        value: `${analytics.interruptions}`,
        status: analytics.interruptions <= 3 ? 'Good' : 'Frequent',
      },
      {
        icon: icons.gauge,
        label: 'Pace',
        value: `${analytics.paceWordsPerMin}`,
        unit: 'words/min',
        status:
          analytics.paceWordsPerMin >= 110 && analytics.paceWordsPerMin <= 160
            ? 'Optimal'
            : analytics.paceWordsPerMin < 110
              ? 'Slow'
              : 'Fast',
      },
      {
        icon: icons.zap,
        label: 'Energy Level',
        value: `${analytics.energyLevel}`,
        status: 'Estimated',
      },
    ];

    const cardsHtml = items
      .map(
        (item) => `
        <div style="background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 10px 6px; display: flex; flex-direction: column; justify-content: space-between; align-items: center; text-align: center; gap: 6px; box-shadow: 0 1px 3px rgba(0,0,0,0.03); min-width: 0;">
          <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; width: 100%;">
            <div style="background: #eff6ff; padding: 4px; border-radius: 6px; display: flex; flex-shrink: 0; margin-bottom: 2px;">${item.icon}</div>
            <span style="font-size: 10px; font-weight: 800; color: #1e40af; text-transform: uppercase; line-height: 1.15; text-align: center; height: 24px; display: flex; align-items: center; justify-content: center;">${item.label}</span>
          </div>
          <div style="display: flex; align-items: baseline; justify-content: center; gap: 2px; margin: 2px 0;">
            <span style="font-size: 15px; font-weight: 800; color: #0f172a; white-space: nowrap;">${item.value}</span>
            ${item.unit ? `<span style="font-size: 9px; color: #64748b; white-space: nowrap;">${item.unit}</span>` : ''}
          </div>
          ${item.status ? `<div style="font-size: 10px; font-weight: 700; color: #2563eb; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%;">${item.status}</div>` : ''}
        </div>
      `,
      )
      .join('');

    return `
      <div class="info-card" style="margin-bottom: 24px;">
        <div class="section-title">
          <div style="background: #dbeafe; padding: 5px; border-radius: 6px; display: flex;">${icons.barChart}</div>
          <span>Conversation Analytics</span>
        </div>
        <div style="display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px;">
          ${cardsHtml}
        </div>
      </div>
    `;
  };

  // Render Transcript Section if text exists (Disabled)

  // Header Subtitle and Metadata depending on Pitch, Scenario, or Meeting
  const subTitleText = isScenario
    ? 'Scenario Roleplay Intelligence Report'
    : isPitch
      ? 'Pitch Delivery & Sales Intelligence Report'
      : 'Sales & Pitch Intelligence Report';

  const isGenericTitle = (val: string) =>
    !val || ['cold', 'cold session', 'call', 'meeting', 'pitch', 'n/a', 'unknown'].includes(val.toLowerCase().trim());

  const metaItems = [
    isScenario
      ? `<div><strong>Scenario:</strong> ${companyName && !isGenericTitle(companyName) ? companyName : 'Roleplay Scenario'}</div>`
      : isPitch
        ? (companyName && !isGenericTitle(companyName) ? `<div><strong>Pitch:</strong> ${companyName}</div>` : '')
        : (companyName && !isGenericTitle(companyName) ? `<div><strong>Company:</strong> ${companyName}</div>` : ''),

    isScenario
      ? (scenarioObj.personaTitle || customerFullName
          ? `<div><strong>Persona:</strong> ${String(scenarioObj.personaTitle || customerFullName)}</div>`
          : '')
      : (customerFullName && customerFullName !== 'N/A'
          ? `<div><strong>Customer:</strong> ${customerFullName}${customerTitle ? ` <span style="color:#64748b;">(${customerTitle})</span>` : ''}</div>`
          : ''),

    repUserName ? `<div><strong>Representative:</strong> ${repUserName}</div>` : '',
    formattedDateTime ? `<div><strong>Date & Time:</strong> ${formattedDateTime}</div>` : '',
    formattedDuration ? `<div><strong>Duration:</strong> ${formattedDuration}</div>` : '',
  ]
    .filter(Boolean)
    .join('');

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Pitchster Report - ${customerFullName || companyName || 'Session'}</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 12mm 0mm;
          }
          @media print {
            html, body {
              margin: 0 !important;
              background: #ffffff !important;
              width: 100% !important;
            }
            body {
              padding: 10mm 12mm !important;
              box-sizing: border-box !important;
            }
            .header-banner {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            .section-title, h1, h2, h3, h4 {
              break-after: avoid !important;
              page-break-after: avoid !important;
            }
            li {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            .category-score-card, .deal-metric-card {
              break-inside: avoid !important;
              page-break-inside: avoid !important;
            }
            .card-box, .info-card, .info-card > div, .coaching-section, .grid-2, div[style*="border-radius"], div[style*="border:"] {
              -webkit-box-decoration-break: clone !important;
              box-decoration-break: clone !important;
            }
          }
          body {
            font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, Helvetica, Arial, sans-serif;
            color: #0f172a;
            background: #ffffff;
            margin: 0 auto;
            padding: 10mm 12mm;
            max-width: 100%;
            box-sizing: border-box;
            font-size: 14px;
            line-height: 1.5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header-banner {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 3px solid #2563eb;
            padding-bottom: 18px;
            margin-bottom: 28px;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .brand-logo {
            height: 48px;
            max-width: 280px;
            object-fit: contain;
            display: block;
            margin-bottom: 6px;
          }
          .brand-title {
            font-size: 30px;
            font-weight: 800;
            color: #1d4ed8;
            margin: 0;
            letter-spacing: 0.02em;
          }
          .sub-title {
            font-size: 14px;
            color: #64748b;
            margin-top: 4px;
            font-weight: 500;
          }
          .meta-box {
            text-align: right;
            font-size: 12.5px;
            color: #334155;
            line-height: 1.8;
            max-width: 55%;
            word-break: break-word;
          }
          .section-title {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 20px;
            font-weight: 800;
            color: #0f172a;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 8px;
            margin-top: 28px;
            margin-bottom: 18px;
            break-after: avoid;
            page-break-after: avoid;
          }
          .grid-2 {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 18px;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
          .card-box {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 18px 20px;
            box-sizing: border-box;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
            margin-bottom: 12px;
          }
          .category-score-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 14px 12px;
            box-sizing: border-box;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .deal-metric-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            padding: 14px 12px;
            box-sizing: border-box;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }
          .coaching-section {
            margin-bottom: 24px;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
          .info-card {
            margin-bottom: 24px;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
          .info-card > div {
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
          div[style*="border-radius"], div[style*="border:"] {
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
          }
          ul {
            padding-left: 24px;
            margin: 8px 0;
            font-size: 14px;
          }
          li {
            break-inside: avoid;
            page-break-inside: avoid;
          }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header-banner">
          <div>
            ${
              logoUrl
                ? `<img src="${logoUrl}" alt="Pitchster" class="brand-logo" />`
                : `<h1 class="brand-title">PITCHSTER</h1>`
            }
            <div class="sub-title">${subTitleText}</div>
          </div>
          <div class="meta-box">
            ${metaItems}
          </div>
        </div>

        <!-- 1. Deal Intelligence Section (With UI Color Logic) -->
        ${renderDealIntelligence()}

        <!-- 2. Skill & Category Scoring Breakdown (Right after Deal Intelligence with SVG Score Rings & UI Color Logic) -->
        ${renderCategoryScoresBreakdown()}

        <!-- 3. Conversation Analytics (Right below Skill & Category Scoring Breakdown) -->
        ${renderConversationAnalyticsSection()}

        <!-- 4. Pitchster Insights Section (Matching UI) -->
        ${renderPitchsterInsightsSection()}

        <!-- 5. Video Analysis -->
        ${renderVideoAnalysisSection(rawData, icons)}

        <!-- 6. Buying Signals -->
        ${renderBuyingSignals()}

        <!-- 7. Objections Detected -->
        ${renderObjectionsDetected()}

        <!-- 8. Missed Opportunities -->
        ${renderMissedOpportunities()}

        <!-- 9. Meeting Notes -->
        ${renderMeetingNotesSection()}

        <!-- Footer -->
        <div style="margin-top: 40px; border-top: 1px solid #e2e8f0; padding-top: 16px; text-align: center; font-size: 12px; color: #94a3b8;">
          Generated by Pitchster Sales Intelligence • Confidential Report
        </div>
      </body>
    </html>
  `;

  try {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const frameWin = iframe.contentWindow;
    const frameDoc = frameWin?.document || iframe.contentDocument;

    if (frameDoc && frameWin) {
      frameDoc.open();
      frameDoc.write(`<!DOCTYPE html>${html}`);
      frameDoc.close();

      setTimeout(() => {
        try {
          frameWin.focus();
          frameWin.print();
        } catch (e) {
          console.error('Print iframe error:', e);
        } finally {
          setTimeout(() => {
            if (document.body.contains(iframe)) {
              document.body.removeChild(iframe);
            }
          }, 1500);
        }
      }, 500);
    } else {
      const printWindow = window.open('', '_blank', 'width=1150,height=1000');
      if (printWindow) {
        printWindow.document.open();
        printWindow.document.write(`<!DOCTYPE html>${html}`);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 500);
      }
    }
  } catch (err) {
    console.error('Export PDF error:', err);
  }
};
